import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import Layout from './layout/Layout';
import { useAuth } from '../context/AuthContext';
import { userApi, bookingApi, paymentApi, twoSvApi, getMyEffectiveOrganisationPermissions } from '../services/api';
import config from '../config';
import { buildAuthHeaders } from '../services/authStorage';
import GoogleIdentityButton from './auth/GoogleIdentityButton';
import { signInWithApple } from '../services/oauthProviders';
import BotSessionKeysPanel from './BotSessionKeysPanel';
import OrgStaticKeysPanel from './OrgStaticKeysPanel';
import { useDialogDraft } from '../hooks/useDialogDraft';
import { buildDraftScopeKey, getDraftForScope, clearDraftForScope } from '../utils/dialogDraftStore';
import Notifications from './Notifications';
import SecurityEvents from './SecurityEvents';
import MobileDownload from './MobileDownload';
import SyncthingPanel from './SyncthingPanel';
import StepUpDialog from './auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import { getTwoSvLoginRequirementState } from '../utils/twoSvProfileState';
import { formatDateForLocalInput } from '../utils/localDateTimeInput';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import './Placeholders.css';
import './BookingsGrid.css';

// ==================== Profile ====================
const Profile = ({ initialTab = 'profile' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: securityEventId } = useParams();
  const {
    user,
    updateUser,
    refreshLoginMethods,
    linkGoogleLoginMethod,
    linkAppleLoginMethod,
    unlinkLoginMethod,
  } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
  });
  const [organisations, setOrganisations] = useState([]);
  const [loginMethods, setLoginMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsMessage, setMethodsMessage] = useState('');
  const [methodsError, setMethodsError] = useState('');
  const [appleLinkLoading, setAppleLinkLoading] = useState(false);
  const [twoSvMethods, setTwoSvMethods] = useState([]);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [twoSvEnforcedForLogin, setTwoSvEnforcedForLogin] = useState(false);
  const [twoSvRequiredByPolicy, setTwoSvRequiredByPolicy] = useState(false);
  const [twoSvCanEnableForLogin, setTwoSvCanEnableForLogin] = useState(false);
  const [twoSvNextAction, setTwoSvNextAction] = useState('none');
  const [twoSvRecoveryMessage, setTwoSvRecoveryMessage] = useState('');
  const [twoSvStepUpChallenge, setTwoSvStepUpChallenge] = useState(null);
  const [twoSvStepUpRetry, setTwoSvStepUpRetry] = useState(null);
  const [twoSvLoading, setTwoSvLoading] = useState(false);
  const [twoSvMessage, setTwoSvMessage] = useState('');
  const [twoSvError, setTwoSvError] = useState('');
  const [totpEnrollment, setTotpEnrollment] = useState(null);
  const [totpQrCodeUrl, setTotpQrCodeUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [privacyExport, setPrivacyExport] = useState(null);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyMessage, setPrivacyMessage] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      fetchOrganisations();
      loadLoginMethods();
      loadTwoSvMethods();
    }
  }, [user]);

  useEffect(() => {
    let isCurrent = true;
    const otpauthUrl = totpEnrollment?.otpauthUrl;

    if (!otpauthUrl) {
      setTotpQrCodeUrl('');
      return undefined;
    }

    QRCode.toDataURL(otpauthUrl, {
      margin: 1,
      scale: 6,
      color: {
        dark: '#111827',
        light: '#ffffff',
      },
    })
      .then((dataUrl) => {
        if (isCurrent) {
          setTotpQrCodeUrl(dataUrl);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setTotpQrCodeUrl('');
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [totpEnrollment?.otpauthUrl]);

  const getApiPayload = (response) => response.data?.data || response.data || {};

  const getApiErrorMessage = (err, fallback) => (
    err.response?.data?.message
    || err.message
    || fallback
  );

  const getEnableTwoSvForLoginErrorMessage = (err) => {
    const payload = err.response?.data || {};
    const errorCode = payload.error || '';
    const details = payload.details || {};
    const reason = String(details.unavailableReason || details.reason || '').toLowerCase();
    const nextAction = details.nextAction || '';

    if (details.recoveryMessage) {
      return details.recoveryMessage;
    }

    if (nextAction === 'enrol_2sv' || errorCode === 'TWO_SV_NOT_ENROLLED') {
      if (hasTwoSvMethods) {
        return 'Your 2-step verification method is not currently available for this action. Re-check your authenticator app or trusted phone, or set up a new authenticator method before enabling 2SV for login.';
      }

      return 'You need to set up at least one 2-step verification method, such as an authenticator app, before you can enable 2SV for login.';
    }

    if (nextAction === 'recover_2sv_method' || errorCode === 'TWO_SV_METHOD_UNAVAILABLE') {
      if (/policy|organisation|organization/.test(reason)) {
        return 'Your organisation security policy does not allow the 2SV methods you have enrolled. Contact your administrator or add an allowed method before enabling 2SV for login.';
      }

      if (/trust|trusted|expired|revoked|broken|device|push/.test(reason)) {
        return 'Your 2-step verification method is not currently available. Re-verify your trusted phone or set up an authenticator app before enabling 2SV for login.';
      }

      return 'Your enrolled 2-step verification method is not available for this action. Manage your 2SV methods, then try enabling 2SV for login again.';
    }

    return getApiErrorMessage(err, 'A fresh two-step verification step-up is required before enabling 2SV for login.');
  };

  const fetchOrganisations = async () => {
    try {
      const response = await fetch('/api/users/me/organisations', {
        headers: buildAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOrganisations(data.data?.organisations || []);
      }
    } catch (err) {
      console.error('Failed to fetch organisations:', err);
    }
  };

  const loadLoginMethods = async () => {
    try {
      setMethodsLoading(true);
      setMethodsError('');
      const methods = await refreshLoginMethods();
      setLoginMethods(methods);
    } catch (err) {
      setMethodsError(err.message || 'Failed to load linked sign-in methods.');
    } finally {
      setMethodsLoading(false);
    }
  };

  const loadTwoSvMethods = async () => {
    try {
      setTwoSvLoading(true);
      setTwoSvError('');
      const payload = getApiPayload(await twoSvApi.getMethods());
      setTwoSvMethods(payload.methods || []);
      setBackupCodesRemaining(payload.backupCodesRemaining || 0);
      setTwoSvEnforcedForLogin(Boolean(payload.enforced));
      setTwoSvRequiredByPolicy(Boolean(payload.required));
      setTwoSvCanEnableForLogin(Boolean(payload.canEnableForLogin));
      setTwoSvNextAction(payload.nextAction || 'none');
      setTwoSvRecoveryMessage(payload.recoveryMessage || '');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'Failed to load two-step verification methods.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const getOnboardingStatusBadge = (status) => {
    const badges = {
      'not_started': { label: 'Not Started', class: 'badge-gray' },
      'in_progress': { label: 'In Progress', class: 'badge-warning' },
      'complete': { label: 'Complete', class: 'badge-success' },
      'expired': { label: 'Expired', class: 'badge-danger' }
    };
    return badges[status] || badges['not_started'];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      await userApi.updateProfile(formData);
      updateUser(formData);
      setMessage('Profile updated successfully!');
    } catch (err) {
      setMessage('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLink = async (response) => {
    try {
      setMethodsMessage('');
      setMethodsError('');
      setMethodsLoading(true);
      await linkGoogleLoginMethod({ idToken: response.credential });
      await loadLoginMethods();
      setMethodsMessage('Google is now linked to your account.');
    } catch (err) {
      setMethodsError(err.message || 'Failed to link Google.');
    } finally {
      setMethodsLoading(false);
    }
  };

  const handleAppleLink = async () => {
    try {
      setMethodsMessage('');
      setMethodsError('');
      setAppleLinkLoading(true);
      const appleResponse = await signInWithApple();
      await linkAppleLoginMethod(appleResponse);
      await loadLoginMethods();
      setMethodsMessage('Apple is now linked to your account.');
    } catch (err) {
      setMethodsError(err.message || 'Failed to link Apple.');
    } finally {
      setAppleLinkLoading(false);
    }
  };

  const handleUnlink = async (provider) => {
    if (!window.confirm(`Unlink ${provider} from this account?`)) {
      return;
    }

    try {
      setMethodsMessage('');
      setMethodsError('');
      setMethodsLoading(true);
      await unlinkLoginMethod(provider);
      await loadLoginMethods();
      setMethodsMessage(`${provider} has been unlinked from your account.`);
    } catch (err) {
      setMethodsError(err.message || `Failed to unlink ${provider}.`);
    } finally {
      setMethodsLoading(false);
    }
  };

  const handleBeginTotpEnrollment = async () => {
    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setBackupCodes([]);
      setTwoSvLoading(true);
      const payload = getApiPayload(await twoSvApi.beginTotp());
      setTotpEnrollment(payload);
      setTotpCode('');
      setTwoSvMessage('Authenticator setup started.');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'Could not start authenticator setup.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const handleConfirmTotpEnrollment = async (e) => {
    e.preventDefault();
    if (!totpEnrollment?.enrolmentChallengeId || !totpCode.trim()) {
      setTwoSvError('Enter the authenticator code to finish setup.');
      return;
    }

    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setTwoSvLoading(true);
      const payload = getApiPayload(await twoSvApi.confirmTotp({
        enrolmentChallengeId: totpEnrollment.enrolmentChallengeId,
        code: totpCode.trim(),
      }));
      setBackupCodes(payload.backupCodes || []);
      setTotpEnrollment(null);
      setTotpCode('');
      await loadTwoSvMethods();
      setTwoSvMessage('Two-step verification is now enabled.');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'Could not confirm the authenticator code.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setTwoSvLoading(true);
      const payload = getApiPayload(await twoSvApi.regenerateBackupCodes());
      setBackupCodes(payload.backupCodes || []);
      await loadTwoSvMethods();
      setTwoSvMessage('Backup codes regenerated.');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'A fresh high-security session is required before regenerating backup codes.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const handleEnableTwoSvForLogin = async () => {
    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setTwoSvLoading(true);
      await twoSvApi.enableLogin();
      await loadTwoSvMethods();
      setTwoSvMessage('Two-step verification is now required when you sign in.');
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setTwoSvStepUpChallenge(challenge);
        setTwoSvStepUpRetry(() => handleEnableTwoSvForLogin);
        return;
      }

      const nextAction = err.response?.data?.details?.nextAction;
      if (nextAction === 'enrol_2sv') {
        setTwoSvError('');
        await handleBeginTotpEnrollment();
        return;
      }

      setTwoSvError(getEnableTwoSvForLoginErrorMessage(err));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const closeTwoSvStepUp = () => {
    setTwoSvStepUpChallenge(null);
    setTwoSvStepUpRetry(null);
  };

  const completeTwoSvStepUp = async () => {
    const retry = twoSvStepUpRetry;
    closeTwoSvStepUp();
    if (retry) {
      await retry();
    }
  };

  const handleRemoveTwoSvMethod = async (method) => {
    if (!window.confirm(`Remove ${method.label || method.method} from this account?`)) {
      return;
    }

    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setTwoSvLoading(true);
      await twoSvApi.removeMethod(method.id);
      await loadTwoSvMethods();
      setTwoSvMessage('Two-step verification method removed.');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'A fresh high-security session is required before removing this method.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const handleDisableTwoSv = async (e) => {
    e.preventDefault();
    if (!disablePassword.trim()) {
      setTwoSvError('Enter your password to disable two-step verification.');
      return;
    }

    try {
      setTwoSvMessage('');
      setTwoSvError('');
      setTwoSvLoading(true);
      await twoSvApi.disable(disablePassword);
      setDisablePassword('');
      setBackupCodes([]);
      await loadTwoSvMethods();
      setTwoSvMessage('Two-step verification disabled.');
    } catch (err) {
      setTwoSvError(getApiErrorMessage(err, 'A fresh high-security session is required before disabling two-step verification.'));
    } finally {
      setTwoSvLoading(false);
    }
  };

  const handleExportAccountData = async () => {
    try {
      setPrivacyLoading(true);
      setPrivacyMessage('');
      const response = await userApi.exportProfile();
      setPrivacyExport(response.data?.data || {});
    } catch (err) {
      setPrivacyMessage(err.response?.data?.error?.message || err.response?.data?.message || 'Account export requires a fresh high-security session.');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.uuid) {
      setPrivacyMessage('Account deletion is unavailable for this session.');
      return;
    }

    if (!window.confirm('Delete your account and revoke web-push subscriptions? This action cannot be undone.')) {
      return;
    }

    try {
      setPrivacyLoading(true);
      setPrivacyMessage('');
      await userApi.deleteUser(user.uuid);
      setPrivacyMessage('Account deletion requested. Web-push subscriptions will be removed by the backend account-delete cascade.');
    } catch (err) {
      setPrivacyMessage(err.response?.data?.error?.message || err.response?.data?.message || 'Account deletion could not be completed from this session.');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const linkedProviders = new Set(loginMethods.map((method) => method.provider));
  const isTwoSvRequiredForRole = ['admin', 'super_admin'].includes(user?.role);
  const hasTwoSvMethods = twoSvMethods.length > 0;
  // Authenticator enrolment must be gated on whether a TOTP method specifically
  // exists, never on "any method exists". GET /auth/2sv/methods returns every
  // confirmed method regardless of whether it can actually satisfy a step-up
  // (twoSv.service.ts listMethods filters on is_confirmed only; the real
  // availability predicate in getMethodAvailability additionally requires the
  // push device to exist, be un-revoked and be currently trusted). A user whose
  // only methods are push rows on untrusted devices therefore reports
  // hasTwoSvMethods === true while holding zero usable factors: gating on it hid
  // the "Set up authenticator app" button and left the account unable to
  // self-enrol a working factor, blocking every SAL-4 action with
  // TWO_SV_METHOD_UNAVAILABLE. Gating on TOTP presence keeps the
  // no-duplicate-enrolment intent (Core rejects a second TOTP with
  // ALREADY_ENROLLED) while leaving the enrolment door open to push/webauthn-only
  // accounts. Enrolment itself remains authenticate + requireActiveStatus on the
  // backend; no step-up or auth behaviour is relaxed here.
  const hasTotpMethod = twoSvMethods.some((method) => method.method === 'totp');
  const isTwoSvRequiredForLogin = isTwoSvRequiredForRole || twoSvEnforcedForLogin || twoSvRequiredByPolicy;
  const appUserRoles = new Set(['App_Test', 'App_A', 'App_B', 'App_Bot_A', 'App_Bot_B']);
  const canManageOwnBotKeys = user?.role !== 'event_simple' && !appUserRoles.has(user?.role);
  const twoSvStatusLabel = isTwoSvRequiredForLogin
    ? (hasTwoSvMethods ? 'Required and on' : 'Required setup')
    : (hasTwoSvMethods ? 'On' : 'Optional');
  const twoSvRequirementText = isTwoSvRequiredForRole
    ? 'Required for admin roles.'
    : (isTwoSvRequiredForLogin ? 'Required when this account signs in.' : 'Optional for this role.');
  const twoSvLoginRequirement = getTwoSvLoginRequirementState({
    enrolled: hasTwoSvMethods,
    enforced: twoSvEnforcedForLogin || twoSvRequiredByPolicy || isTwoSvRequiredForRole,
    canEnableForLogin: twoSvCanEnableForLogin,
    nextAction: twoSvNextAction,
    recoveryMessage: twoSvRecoveryMessage,
  });
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const eventIdParam = searchParams.get('id');
  const validTabs = new Set(['profile', 'sign-in', 'notifications', 'security', 'mobile', 'sync']);
  const initialProfileTab = validTabs.has(initialTab) ? initialTab : 'profile';
  const activeTab = validTabs.has(tabParam) ? tabParam : initialProfileTab;
  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'sign-in', label: 'Sign-In Methods' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'security', label: 'Security Events' },
    { id: 'mobile', label: 'Mobile App' },
    { id: 'sync', label: 'Sync' },
  ];

  const selectTab = (tabId) => {
    navigate(tabId === 'profile' ? '/app/profile' : `/app/profile?tab=${tabId}`);
  };

  return (
    <Layout>
      <div className="profile-page">
        <header className="page-header">
          <h1>Account</h1>
          <p className="subtitle">Manage profile, notifications, security activity, and mobile access</p>
        </header>

        <div className="profile-tabs" role="tablist" aria-label="Account sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'profile' ? (
        <div className="profile-content">
          <div className="profile-card">
            <div className="profile-avatar">
              <span>{user?.displayName?.[0] || '?'}</span>
            </div>
            <h2>{user?.displayName} <span style={{ fontSize: '0.6em', color: 'var(--text-secondary)', fontWeight: 'normal' }}>#{user?.id}</span></h2>
            <p className="role-label">{user?.role}</p>
          </div>

          <form className="profile-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="displayName">Display Name</label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                value={formData.displayName}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                disabled
              />
              <span className="form-hint">Email cannot be changed</span>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your phone number"
              />
            </div>

            {message && (
              <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>

          {/* M004-F0086: Mobile App Download Card */}
          <div className="mobile-app-card">
            <div className="mobile-app-header">
              <span className="mobile-app-icon">📱</span>
              <h3>Get the StudioAEye Mobile App</h3>
            </div>
            <p className="mobile-app-description">
              Access your bookings, control room climate, and manage your schedule from your mobile device.
            </p>
            <div className="mobile-app-buttons">
              <a 
                href="/downloads/studioaeye.apk" 
                className="btn btn-primary mobile-download-btn"
                download
              >
                <span className="btn-icon">🤖</span>
                Download for Android
              </a>
              <button className="btn btn-outline mobile-download-btn" disabled>
                <span className="btn-icon">🍎</span>
                iOS Coming Soon
              </button>
            </div>
            <p className="mobile-app-hint">
              <small>Android APK • Requires Android 8.0 or later</small>
            </p>
          </div>

          {/* Organisations Section */}
          {organisations.length > 0 && (
            <div className="organisations-section">
              <h3>Your Organisations</h3>
              <div className="organisations-list">
                {organisations.map((org) => (
                  <div key={org.organisation_id} className="organisation-card">
                    <div className="org-header">
                      <h4>{org.organisation_name}</h4>
                      <span className={`role-badge ${org.role}`}>{org.role}</span>
                    </div>
                    <div className="org-details">
                      <div className="detail-row">
                        <span className="detail-label">Onboarding Status:</span>
                        <span className={`status-badge ${getOnboardingStatusBadge(org.onboarding_status).class}`}>
                          {getOnboardingStatusBadge(org.onboarding_status).label}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Joined:</span>
                        <span>{new Date(org.joined_at).toLocaleDateString()}</span>
                      </div>
                      {org.onboarding_status !== 'complete' && (
                        <div className="onboarding-warning">
                          ⚠️ Complete onboarding to book rooms without approval
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <section className="profile-panel privacy-panel">
            <div className="profile-panel__header">
              <div>
                <h3>Privacy</h3>
                <p>Export account data and review web-push token metadata included in the GDPR payload.</p>
              </div>
              <button type="button" className="btn btn-outline" onClick={handleExportAccountData} disabled={privacyLoading}>
                {privacyLoading ? 'Exporting...' : 'Export account data'}
              </button>
            </div>

            {privacyMessage ? <div className="message error">{privacyMessage}</div> : null}

            {privacyExport && (
              <div className="privacy-export">
                <h4>Push token export</h4>
                {Array.isArray(privacyExport.devicePushTokens) && privacyExport.devicePushTokens.length > 0 ? (
                  <div className="privacy-token-list">
                    {privacyExport.devicePushTokens.map((token) => (
                      <article key={token.uuid || token.tokenLast8} className="privacy-token-card">
                        <strong>{token.channel || 'push'} on {token.platform || 'unknown platform'}</strong>
                        <span>Token ending {token.tokenLast8 || 'unknown'}</span>
                        <span>{token.disabledAt ? `Disabled ${new Date(token.disabledAt).toLocaleString()}` : 'Active'}</span>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="form-hint">No web-push token rows were returned in this export.</p>
                )}
                <details>
                  <summary>View export JSON</summary>
                  <pre>{JSON.stringify(privacyExport, null, 2)}</pre>
                </details>
              </div>
            )}
          </section>

          <div className="danger-zone">
            <h3>Danger Zone</h3>
            <p>Once you delete your account, there is no going back. Account deletion also removes web-push subscriptions on the backend.</p>
            <button type="button" className="btn btn-danger" onClick={handleDeleteAccount} disabled={privacyLoading}>
              Delete Account
            </button>
          </div>
        </div>
        ) : activeTab === 'sign-in' ? (
          <div className="profile-content" data-testid="profile-sign-in-tab">
            <section className="profile-panel">
              <div className="profile-panel__header">
                <div>
                  <h3>Sign-In Methods</h3>
                  <p>Link Google or Apple so the same account can be used across password, provider, and magic-link sign-in.</p>
                </div>
                <button type="button" className="btn btn-outline" onClick={loadLoginMethods} disabled={methodsLoading || appleLinkLoading}>
                  {methodsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {methodsMessage ? <div className="message success">{methodsMessage}</div> : null}
              {methodsError ? <div className="message error">{methodsError}</div> : null}

              <div className="login-methods-list">
                {loginMethods.map((method) => (
                  <article key={`${method.provider}-${method.email || 'none'}`} className="login-method-card">
                    <div>
                      <strong>{method.provider === 'email' ? 'Email + password' : method.provider === 'google' ? 'Google' : 'Apple'}</strong>
                      <p>{method.email || user?.email || 'No email available'}</p>
                      <span className="login-method-meta">
                        {method.isVerified ? 'Verified' : 'Pending verification'}
                        {method.isPrimaryEmail ? ' · Primary email' : ''}
                        {method.lastUsedAt ? ` · Last used ${new Date(method.lastUsedAt).toLocaleDateString()}` : ''}
                      </span>
                    </div>

                    {method.provider !== 'email' ? (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => handleUnlink(method.provider)}
                        disabled={methodsLoading || appleLinkLoading}
                      >
                        Unlink
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>

              <div className="login-method-actions">
                {!linkedProviders.has('google') ? (
                  <GoogleIdentityButton
                    onCredential={handleGoogleLink}
                    disabled={methodsLoading || appleLinkLoading}
                    width={320}
                    fallbackLabel="Google linking unavailable"
                  />
                ) : null}

                {!linkedProviders.has('apple') ? (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleAppleLink}
                    disabled={appleLinkLoading || methodsLoading || !config.oauth.appleClientId}
                  >
                    {appleLinkLoading ? 'Linking Apple...' : config.oauth.appleClientId ? 'Link Apple' : 'Apple linking unavailable'}
                  </button>
                ) : null}
              </div>

              <div className="two-sv-panel">
                <div className="two-sv-header">
                  <div>
                    <h4>Two-step verification</h4>
                    <p>{twoSvRequirementText}</p>
                  </div>
                  <span className={`two-sv-status ${hasTwoSvMethods ? 'is-on' : 'is-off'}`}>
                    {twoSvStatusLabel}
                  </span>
                </div>

                {twoSvMessage ? <div className="message success">{twoSvMessage}</div> : null}
                {twoSvError ? <div className="message error">{twoSvError}</div> : null}

                {hasTwoSvMethods ? (
                  <div className="two-sv-method-list">
                    {twoSvMethods.map((method) => (
                      <article key={method.id} className="two-sv-method-card">
                        <div>
                          <strong>{method.label || (method.method === 'totp' ? 'Authenticator app' : method.method)}</strong>
                          <span>{method.method === 'totp' ? 'TOTP' : method.method}</span>
                          <small>
                            {method.confirmedAt ? `Confirmed ${new Date(method.confirmedAt).toLocaleDateString()}` : 'Pending confirmation'}
                            {method.lastUsedAt ? ` · Last used ${new Date(method.lastUsedAt).toLocaleDateString()}` : ''}
                          </small>
                        </div>
                        {twoSvMethods.length > 1 ? (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => handleRemoveTwoSvMethod(method)}
                            disabled={twoSvLoading}
                          >
                            Remove
                          </button>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="form-hint">No two-step verification methods are enrolled.</p>
                )}

                {twoSvLoginRequirement.visible ? (
                  <div
                    className={`two-sv-enable-login ${twoSvLoginRequirement.disabled ? 'is-disabled' : ''}`}
                    data-testid="two-sv-enable-login-prompt"
                    title={twoSvLoginRequirement.disabled ? twoSvLoginRequirement.recoveryMessage : undefined}
                  >
                    <div>
                      <strong>Use 2SV every time you sign in</strong>
                      <p
                        id="two-sv-enable-login-guidance"
                        data-testid="two-sv-enable-login-guidance"
                      >
                        {twoSvLoginRequirement.disabled
                          ? twoSvLoginRequirement.recoveryMessage
                          : 'Authenticator approval is set up. Turn on the sign-in requirement so this account asks for 2SV during login.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleEnableTwoSvForLogin}
                      disabled={twoSvLoading || twoSvLoginRequirement.disabled}
                      aria-describedby="two-sv-enable-login-guidance"
                      data-testid="two-sv-enable-login-button"
                    >
                      {twoSvLoading ? 'Enabling...' : 'Enable 2SV for login'}
                    </button>
                  </div>
                ) : null}

                <div className="two-sv-summary">
                  <span>Backup codes remaining</span>
                  <strong>{backupCodesRemaining}</strong>
                </div>

                {totpEnrollment ? (
                  <form className="two-sv-enrollment" onSubmit={handleConfirmTotpEnrollment}>
                    {totpQrCodeUrl ? (
                      <div className="two-sv-qr">
                        <img src={totpQrCodeUrl} alt="Authenticator app QR code" />
                      </div>
                    ) : null}
                    <div className="form-group">
                      <label htmlFor="totp-secret">Secret key</label>
                      <input
                        id="totp-secret"
                        type="text"
                        className="form-input"
                        value={totpEnrollment.secret || ''}
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="totp-url">Authenticator setup URL</label>
                      <textarea
                        id="totp-url"
                        className="form-input two-sv-url"
                        value={totpEnrollment.otpauthUrl || ''}
                        readOnly
                      />
                    </div>
                    <div className="two-sv-inline-form">
                      <div className="form-group">
                        <label htmlFor="totp-code">Verification code</label>
                        <input
                          id="totp-code"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          className="form-input"
                          value={totpCode}
                          onChange={(event) => setTotpCode(event.target.value)}
                          maxLength={8}
                        />
                      </div>
                      <button type="submit" className="btn btn-primary" disabled={twoSvLoading}>
                        Confirm
                      </button>
                    </div>
                  </form>
                ) : null}

                {backupCodes.length > 0 ? (
                  <div className="two-sv-backup-codes" aria-label="New backup codes">
                    {backupCodes.map((code) => (
                      <code key={code}>{code}</code>
                    ))}
                  </div>
                ) : null}

                <div className="two-sv-actions">
                  {!hasTotpMethod && !totpEnrollment ? (
                    <button type="button" className="btn btn-primary" onClick={handleBeginTotpEnrollment} disabled={twoSvLoading}>
                      Set up authenticator app
                    </button>
                  ) : null}

                  {hasTwoSvMethods ? (
                    <button type="button" className="btn btn-outline" onClick={handleRegenerateBackupCodes} disabled={twoSvLoading}>
                      Regenerate backup codes
                    </button>
                  ) : null}
                </div>

                {hasTwoSvMethods && !isTwoSvRequiredForRole ? (
                  <form className="two-sv-disable-form" onSubmit={handleDisableTwoSv}>
                    <label htmlFor="two-sv-disable-password">Password</label>
                    <div className="two-sv-inline-form">
                      <input
                        id="two-sv-disable-password"
                        type="password"
                        className="form-input"
                        value={disablePassword}
                        onChange={(event) => setDisablePassword(event.target.value)}
                        autoComplete="current-password"
                      />
                      <button type="submit" className="btn btn-outline" disabled={twoSvLoading}>
                        Disable
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            </section>

            {canManageOwnBotKeys ? (
              <BotSessionKeysPanel
                title="Static Session Keys"
                description="Create read-only or read/write keys for your own automation. Stored keys never expose their raw secret after creation."
                testId="profile-static-session-keys"
              />
            ) : null}

            <OrgStaticKeysPanel testId="profile-org-static-keys" />
          </div>
        ) : activeTab === 'notifications' ? (
          <div className="profile-content">
            <Notifications embedded />
          </div>
        ) : activeTab === 'security' ? (
          <div className="profile-content">
            <SecurityEvents embedded eventId={eventIdParam || securityEventId} />
          </div>
        ) : activeTab === 'mobile' ? (
          <div className="profile-content">
            <MobileDownload embedded />
          </div>
        ) : activeTab === 'sync' ? (
          <div className="profile-content">
            <SyncthingPanel embedded />
          </div>
        ) : null}
      </div>
      <StepUpDialog
        challenge={twoSvStepUpChallenge}
        title="2SV login change needs verification"
        description="Requiring 2SV at login needs a fresh two-step verification before the setting can be saved."
        onComplete={completeTwoSvStepUp}
        onCancel={closeTwoSvStepUp}
      />
    </Layout>
  );
};

// ==================== Booking Price Section: RETIRED ====================
// M004-F0680 (WebUI #108, M004-F0637 §2): the price section that lived here
// (M004-F0616 §B: quote + Recalculate + the `?focus=pricing` deep-link consumer,
// on the retired getBookingQuote/repriceBooking wire) is DELETED. The ONE price
// control is the Bookings Hub drawer's Price section (bookings-admin/
// BookingPriceSection.js, M004-F0648). `formatMoneyMinor` went with it: its only
// caller was this section.

// ==================== Bookings ====================
// M004-F0655 (WebUI #123): the detail route of the My Bookings page
// (registered in App.js as /app/bookingsAdmin/:id).
export const myBookingDetailHref = (bookingId) => `/app/bookingsAdmin/${encodeURIComponent(bookingId)}`;

const Bookings = () => {
  const { id } = useParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  // M004-F0613 / T-M004-0020: Edit Booking is edit-only (no "new" variant here).
  const editBookingDraft = useDialogDraft('placeholder-edit-booking', selectedBooking?.id, { active: showEditModal });

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const editModalDismissHandlers = useDismissOnOutsideClick(() => setShowEditModal(false));

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookingApi.getMyBookings();
      setBookings(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    // M004-F0655 (WebUI #123): /app/bookingsAdmin and /app/bookingsAdmin/:id
    // render this SAME component instance (React reuses it across the two
    // routes), so leaving the detail route must clear the selection or the
    // "Back to Bookings" link would keep showing the detail.
    if (!id) {
      setSelectedBooking(null);
      return;
    }
    if (bookings.length > 0) {
      const booking = bookings.find(b => b.id === parseInt(id));
      setSelectedBooking(booking || null);
    }
  }, [id, bookings]);

  // M004-F0678 (WebUI #108, M004-F0637 §2 redirect, scoped by #123): this page
  // is the member's detail of a booking. For a caller who can manage every
  // booking of the booking's organisation (`bookings_manage_all`, the hub's
  // Studio key — a permission, never a role name) the ONE per-booking surface
  // is the Bookings Hub drawer, so the page forwards there with that booking
  // open. This is also where Core's admin notifications land: Core emits
  // /app/bookingsAdmin/<id>?focus=pricing and is not this lane's to change.
  // A member (no such key) keeps this page, unchanged. The check fails CLOSED:
  // a failed permission read leaves the page, never a wrong redirect.
  const [hubTargetId, setHubTargetId] = useState(null);
  const selectedOrgId = selectedBooking?.organisation_id ?? null;
  const selectedBookingId = selectedBooking?.id ?? null;
  useEffect(() => {
    setHubTargetId(null);
    if (selectedOrgId === null || selectedBookingId === null) return undefined;
    let current = true;
    Promise.resolve(getMyEffectiveOrganisationPermissions(selectedOrgId))
      .then((permissions) => {
        if (current && permissions?.bookings_manage_all === true) setHubTargetId(selectedBookingId);
      })
      .catch(() => {});
    return () => { current = false; };
  }, [selectedOrgId, selectedBookingId]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'status-badge approved';
      case 'pending': return 'status-badge pending';
      case 'rejected': return 'status-badge rejected';
      case 'cancelled': return 'status-badge cancelled';
      default: return 'status-badge';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✓';
      case 'pending': return '⏳';
      case 'rejected': return '✗';
      case 'cancelled': return '🚫';
      default: return '';
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }
    
    setCancelling(true);
    try {
      await bookingApi.cancelBooking(bookingId);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      alert('Failed to cancel booking. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to permanently delete this booking? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await bookingApi.deleteBooking(bookingId);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err) {
      console.error('Failed to delete booking:', err);
      alert('Failed to delete booking. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (booking) => {
    // M004-F0622 / T-M004-0032: `formatDateForLocalInput` builds the
    // datetime-local value from LOCAL date components, matching exactly what
    // `new Date(editForm.start_time)` will parse it back as on save below —
    // a lossless round trip. The previous `date.toISOString().slice(0, 16)`
    // showed raw UTC digits mislabelled as local, so an untouched save
    // re-saved them as local->UTC and shifted every booking's stored time by
    // the caller's UTC offset (exactly one hour for BST).
    const draft = getDraftForScope(buildDraftScopeKey('placeholder-edit-booking', booking.id));
    setEditForm({
      title: booking.title,
      description: booking.description || '',
      start_time: formatDateForLocalInput(booking.start_time),
      end_time: formatDateForLocalInput(booking.end_time),
      ...draft,
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);

    try {
      const response = await bookingApi.updateBooking(selectedBooking.id, {
        title: editForm.title,
        description: editForm.description,
        // `new Date(editForm.start_time)` parses this naive datetime-local
        // string as LOCAL time — must be paired with formatDateForLocalInput
        // above, never with a UTC-digit formatter, or the round trip shifts.
        start_time: new Date(editForm.start_time).toISOString(),
        end_time: new Date(editForm.end_time).toISOString()
      });

      setShowEditModal(false);
      clearDraftForScope(buildDraftScopeKey('placeholder-edit-booking', selectedBooking.id));
      fetchBookings();
      
      // Show success message with status info
      if (response.data.data.status === 'pending') {
        alert('Booking updated and sent for approval.');
      } else {
        alert('Booking updated successfully.');
      }
    } catch (err) {
      console.error('Failed to update booking:', err);
      setEditError(err.response?.data?.message || 'Failed to update booking. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  if (hubTargetId !== null) {
    return <Navigate to={`/app/bookings?booking=${encodeURIComponent(hubTargetId)}`} replace />;
  }

  if (selectedBooking) {
    const canEdit = selectedBooking.status !== 'cancelled' && selectedBooking.status !== 'rejected';
    
    return (
      <Layout>
        <div className="booking-detail">
          {/* M004-F0616: was "/app/bookings", which App.js never registers
              (only "/app/bookingsAdmin"/"/app/bookingsAdmin/:id" are) — a
              pre-existing dead link, fixed here per the design doc note
              since this same file/component is already being touched. */}
          <Link to="/app/bookingsAdmin" className="back-link">← Back to Bookings</Link>
          <div className="booking-card-detail">
            <div className={getStatusBadgeClass(selectedBooking.status)}>
              {getStatusIcon(selectedBooking.status)} {selectedBooking.status}
            </div>
            <h1>{selectedBooking.title}</h1>
            <div className="booking-info">
              <p><strong>📍 Room:</strong> {selectedBooking.room_name}</p>
              <p><strong>📅 Start:</strong> {formatDateTime(selectedBooking.start_time)}</p>
              <p><strong>🕐 End:</strong> {formatDateTime(selectedBooking.end_time)}</p>
              {selectedBooking.description && (
                <p><strong>📝 Description:</strong> {selectedBooking.description}</p>
              )}
              {selectedBooking.rejection_reason && (
                <div className="rejection-reason">
                  <strong>❌ Rejection Reason:</strong> {selectedBooking.rejection_reason}
                </div>
              )}
            </div>
            <div className="booking-actions">
              {canEdit && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => openEditModal(selectedBooking)}
                  >
                    ✏️ Edit Booking
                  </button>
                  <button 
                    className="btn btn-outline btn-danger" 
                    onClick={() => cancelBooking(selectedBooking.id)}
                    disabled={cancelling}
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel Booking'}
                  </button>
                </>
              )}
              <button 
                className="btn btn-danger" 
                onClick={() => deleteBooking(selectedBooking.id)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal-overlay" {...editModalDismissHandlers}>
            <div className="modal" ref={editBookingDraft.containerRef} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit Booking</h2>
                <button className="close-btn" onClick={() => setShowEditModal(false)}>✕</button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  {editError && (
                    <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                      {editError}
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="edit-title">Title *</label>
                    <input
                      id="edit-title"
                      type="text"
                      className="form-input"
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-description">Description</label>
                    <textarea
                      id="edit-description"
                      className="form-input"
                      rows="3"
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-start">Start Time *</label>
                    <input
                      id="edit-start"
                      type="datetime-local"
                      className="form-input"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-end">End Time *</label>
                    <input
                      id="edit-end"
                      type="datetime-local"
                      className="form-input"
                      value={editForm.end_time}
                      onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                      required
                    />
                  </div>
                  <p className="form-hint" style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    ⚠️ Note: Editing may require admin approval depending on your onboarding status.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={editLoading}>
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bookings-page">
        <header className="page-header">
          <div>
            <h1>My Bookings</h1>
            <p className="subtitle">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</p>
          </div>
          <Link to="/app/book-room" className="btn btn-primary">+ New Booking</Link>
        </header>

        {error && (
          <div className="alert alert-error">
            {error}
            <button className="btn btn-sm" onClick={fetchBookings}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <span>Loading your bookings...</span>
          </div>
        ) : bookings.length > 0 ? (
          <div className="bookings-grid">
            {bookings.map((booking) => (
              // M004-F0655 (WebUI #123): the row opens THIS page's own detail
              // route. It used to link to /app/bookings/<id>, which App.js
              // never registers, so the catch-all redirected to "/".
              <Link key={booking.id} to={myBookingDetailHref(booking.id)} className={`booking-card booking-${booking.status}`}>
                <div className="booking-header">
                  <h3>{booking.title}</h3>
                  <span className={getStatusBadgeClass(booking.status)}>
                    {getStatusIcon(booking.status)} {booking.status}
                  </span>
                </div>
                {/* M007-F010: Recurring indicator */}
                {(booking.booking_type === 'recurring_instance' || booking.booking_type === 'recurring' || booking.google_recurring_event_id) && (
                  <div className="booking-recurring-badge">🔁 Recurring series</div>
                )}
                <div className="booking-body">
                  <p className="booking-room">📍 {booking.room_name}</p>
                  <p className="booking-time">📅 {formatDateTime(booking.start_time)}</p>
                  <p className="booking-time">🕐 {formatDateTime(booking.end_time)}</p>
                </div>
                {booking.rejection_reason && (
                  <div className="booking-footer rejection">
                    <small>❌ {booking.rejection_reason}</small>
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No bookings yet</h3>
            <p>You haven't made any room bookings yet.</p>
            <Link to="/app/book-room" className="btn btn-primary">Book a Room</Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ==================== Payments ====================
const Payments = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data
    setInvoices([
      { id: 'INV-001', description: 'Monthly Membership - December', amount: 45.00, date: '2025-12-01', status: 'paid' },
      { id: 'INV-002', description: 'Private Lesson - Ballet', amount: 30.00, date: '2025-12-15', status: 'paid' },
      { id: 'INV-003', description: 'Monthly Membership - January', amount: 45.00, date: '2025-12-22', status: 'pending' },
    ]);
    setLoading(false);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalPaid = invoices.filter((i) => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const totalPending = invoices.filter((i) => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);

  return (
    <Layout>
      <div className="payments-page">
        <header className="page-header">
          <h1>Payment History</h1>
          <p className="subtitle">View your invoices and payment status</p>
        </header>

        <div className="payment-summary">
          <div className="summary-card">
            <span className="summary-value">£{totalPaid.toFixed(2)}</span>
            <span className="summary-label">Total Paid</span>
          </div>
          <div className="summary-card pending">
            <span className="summary-value">£{totalPending.toFixed(2)}</span>
            <span className="summary-label">Pending</span>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : invoices.length > 0 ? (
          <div className="invoices-list">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-card">
                <div className="invoice-left">
                  <span className="invoice-id">{invoice.id}</span>
                  <h3>{invoice.description}</h3>
                  <p>{formatDate(invoice.date)}</p>
                </div>
                <div className="invoice-right">
                  <span className="invoice-amount">£{invoice.amount.toFixed(2)}</span>
                  <span className={`status-badge ${invoice.status}`}>{invoice.status}</span>
                  {invoice.status === 'pending' && (
                    <button className="btn btn-primary btn-sm">Pay Now</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <h3>No invoices</h3>
            <p>You don't have any invoices yet.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export { Profile, Bookings, Payments };
